/**
 * CheckoutConfirmationPage - Booking confirmation after successful payment
 * Shows booking details, receipt, and next steps
 */

import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { formatTimeDisplay } from '@sda/shared';
import {
  getBookingConfirmation,
  confirmBooking,
  formatPrice,
  formatBalanceDueDate,
  daysUntilDue,
  type BookingConfirmation,
} from '../../api/booking';
import { getFriendlyErrorMessage } from '../../api/client-error';
import { DashboardMobileNav } from '../../components/dashboard';

export default function CheckoutConfirmationPage() {
  const { user, logout } = useAuth();
  const { bookingId } = useParams<{ bookingId: string }>();
  const [confirmation, setConfirmation] = useState<BookingConfirmation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadConfirmation = async () => {
      if (!bookingId) return;

      try {
        setIsLoading(true);
        setError(null);

        // Check if we're returning from Stripe redirect
        const urlParams = new URLSearchParams(window.location.search);
        const paymentIntentId = urlParams.get('payment_intent');

        if (paymentIntentId) {
          // Confirm the booking with the payment intent
          await confirmBooking(bookingId, paymentIntentId);

          // Clean up URL
          window.history.replaceState({}, '', `/checkout/confirmation/${bookingId}`);
        }

        // Always fetch full confirmation data (confirm endpoint returns minimal data)
        const data = await getBookingConfirmation(bookingId);
        setConfirmation(data);
      } catch (err) {
        setError(getFriendlyErrorMessage(err, 'Failed to load confirmation'));
      } finally {
        setIsLoading(false);
      }
    };

    loadConfirmation();
  }, [bookingId]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-warm-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent mx-auto mb-4" />
          <p className="text-warm-600">Loading your confirmation...</p>
        </div>
      </div>
    );
  }

  if (error || !confirmation) {
    return (
      <div className="min-h-screen bg-warm-50 flex items-center justify-center">
        <div className="card max-w-md mx-auto text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-danger-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-danger-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-warm-900 mb-2">Unable to Load Confirmation</h2>
          <p className="text-warm-600 mb-6">{error || 'Booking not found'}</p>
          <Link to="/dashboard/bookings" className="btn-primary">
            View My Bookings
          </Link>
        </div>
      </div>
    );
  }

  const daysUntil = confirmation.balanceDueDate ? daysUntilDue(confirmation.balanceDueDate) : 0;

  return (
    <div className="min-h-screen bg-warm-50">
      {/* Header */}
      <header className="bg-white border-b border-warm-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2">
              <img src="/logo.png" alt="Stage Door Access" className="h-7" />
            </Link>

            <nav className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="text-warm-600 hover:text-warm-900 text-sm">
                Dashboard
              </Link>
              <Link to="/dashboard/bookings" className="text-warm-600 hover:text-warm-900 text-sm">
                My Bookings
              </Link>
              <Link to="/dashboard/inquiries" className="text-warm-600 hover:text-warm-900 text-sm">
                My Inquiries
              </Link>
              <Link to="/dashboard/profile" className="text-warm-600 hover:text-warm-900 text-sm">
                Studio Profile
              </Link>
              <Link to="/dashboard/contacts" className="text-warm-600 hover:text-warm-900 text-sm">
                Contacts
              </Link>
            </nav>

            <div className="flex items-center gap-4">
              <span className="text-warm-600 text-sm hidden sm:block">{user?.email}</span>
              <button
                onClick={() => logout()}
                className="text-warm-600 hover:text-warm-900 text-sm font-medium"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>
      <DashboardMobileNav />

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Success Banner */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-success-100 flex items-center justify-center">
            <svg className="w-10 h-10 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-3xl font-display font-bold text-warm-900 mb-2">
            Booking Confirmed!
          </h1>
          <p className="text-warm-600">
            Thank you for your booking. A confirmation email has been sent to your email address.
          </p>
        </div>

        {/* Confirmation Number */}
        <div className="card mb-6 text-center bg-primary-50 border-primary-200">
          <p className="text-sm text-primary-700 mb-1">Confirmation Number</p>
          <p className="text-2xl font-mono font-bold text-primary-900">
            {confirmation.confirmationNumber}
          </p>
        </div>

        {/* What's Next */}
        {(() => {
          let step = 0;
          return (
            <div className="card mb-6 bg-warm-50">
              <h3 className="font-semibold text-warm-900 mb-4">What's Next?</h3>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-primary-600 font-semibold text-sm">{++step}</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-warm-900">Check Your Email</h4>
                    <p className="text-sm text-warm-600">
                      We've sent a confirmation email with all your booking details.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-primary-600 font-semibold text-sm">{++step}</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-warm-900">Event Manager Introduction</h4>
                    <p className="text-sm text-warm-600">
                      An SDA Event Manager will be in touch shortly to introduce themselves as your primary point of contact for your class(es).
                    </p>
                  </div>
                </div>
                {confirmation.marketingFeeCents > 0 && (
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-primary-600 font-semibold text-sm">{++step}</span>
                    </div>
                    <div>
                      <h4 className="font-medium text-warm-900">Marketing Materials</h4>
                      <p className="text-sm text-warm-600">
                        You'll find a short questionnaire in your studio dashboard to help us create your custom marketing materials. We'll use your answers to design everything you need to promote your classes.
                      </p>
                    </div>
                  </div>
                )}
                {confirmation.groupTicketInquiry && (
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-primary-600 font-semibold text-sm">{++step}</span>
                    </div>
                    <div>
                      <h4 className="font-medium text-warm-900">Group Ticket Sales</h4>
                      <p className="text-sm text-warm-600">
                        A member of our team will be in touch with information on group ticket sales and pricing for your event.
                      </p>
                    </div>
                  </div>
                )}
                {confirmation.balanceAmountCents > 0 && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-primary-600 font-semibold text-sm">{++step}</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-warm-900">Balance Payment</h4>
                    <p className="text-sm text-warm-600">
                      Your remaining balance of {formatPrice(confirmation.balanceAmountCents)} will be automatically
                      charged to your card on file 15 days before your class.
                    </p>
                  </div>
                </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Show & Venue Info */}
        <div className="card mb-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
              {confirmation.showLogoUrl ? (
                <img
                  src={confirmation.showLogoUrl}
                  alt={confirmation.showName}
                  className="w-full h-full object-cover rounded-lg"
                />
              ) : (
                <span className="text-primary-500 text-2xl font-display font-bold">
                  {confirmation.showName.charAt(0)}
                </span>
              )}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-warm-900">
                Broadway Touring Masterclass with a cast member from {confirmation.showName}
              </h2>
              <p className="text-warm-600">
                {[confirmation.venueCity, confirmation.venueState].filter(Boolean).join(', ')}
                {(confirmation.venueCity || confirmation.venueState) && ' – '}
                {new Date((confirmation.classes[0]?.scheduledDate ?? '') + 'T12:00:00').toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
            </div>
          </div>
        </div>

        {/* Booked Classes */}
        <div className="card mb-6">
          <h3 className="font-semibold text-warm-900 mb-4">Your Classes</h3>
          <div className="space-y-4">
            {confirmation.classes.map((cls) => (
              <div
                key={cls.id}
                className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-warm-50 rounded-lg"
              >
                <div>
                  <h4 className="font-medium text-warm-900">{cls.className}</h4>
                  <p className="text-sm text-warm-600">
                    {new Date(cls.scheduledDate + 'T12:00:00').toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </p>
                  <p className="text-sm text-warm-500">
                    {formatTimeDisplay(cls.startTime)}
                    {cls.studentCount && ` • ${cls.studentCount} students`}
                  </p>
                </div>
                <div className="mt-2 sm:mt-0 text-right">
                  <span className="font-semibold text-warm-900">
                    {formatPrice(cls.totalPriceCents)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Payment Summary */}
        <div className="card mb-6">
          <h3 className="font-semibold text-warm-900 mb-4">Payment Summary</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-warm-600">Subtotal</span>
              <span className="text-warm-900">{formatPrice(confirmation.subtotalCents)}</span>
            </div>
            {confirmation.travelSurchargeCents > 0 && (
              <div className="flex justify-between">
                <span className="text-warm-600">Travel Surcharge</span>
                <span className="text-warm-900">{formatPrice(confirmation.travelSurchargeCents)}</span>
              </div>
            )}
            {confirmation.marketingFeeCents > 0 && (
              <div className="flex justify-between">
                <span className="text-warm-600">Marketing Materials</span>
                <span className="text-warm-900">{formatPrice(confirmation.marketingFeeCents)}</span>
              </div>
            )}
            {confirmation.discountCents > 0 && (
              <div className="flex justify-between text-success-600">
                <span>Discount{confirmation.couponCode ? ` (${confirmation.couponCode})` : ''}</span>
                <span>-{formatPrice(confirmation.discountCents)}</span>
              </div>
            )}
            <div className="pt-2 border-t border-warm-200">
              <div className="flex justify-between font-semibold">
                <span className="text-warm-900">Total</span>
                <span className="text-warm-900">{formatPrice(confirmation.totalAmountCents)}</span>
              </div>
            </div>
            {confirmation.balanceAmountCents > 0 ? (
              <>
                <div className="pt-2 border-t border-warm-200">
                  <div className="flex justify-between text-success-600">
                    <span>Deposit Paid</span>
                    <span>-{formatPrice(confirmation.depositPaidCents)}</span>
                  </div>
                </div>
                <div className="pt-2 border-t border-warm-200">
                  <div className="flex justify-between font-semibold text-primary-600">
                    <span>Balance Due</span>
                    <span>{formatPrice(confirmation.balanceAmountCents)}</span>
                  </div>
                  {confirmation.balanceDueDate && (
                    <p className="text-xs text-warm-500 mt-1">
                      Due by {formatBalanceDueDate(confirmation.balanceDueDate)} ({daysUntil} days)
                    </p>
                  )}
                </div>
              </>
            ) : (
              <div className="pt-2 border-t border-warm-200">
                <div className="flex items-center justify-between font-semibold text-success-600">
                  <span className="flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Paid in Full
                  </span>
                  <span>{formatPrice(confirmation.totalAmountCents)}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Link to="/dashboard/bookings" className="btn-primary flex-1 text-center">
            View My Bookings
          </Link>
          <Link to="/" className="btn-secondary flex-1 text-center">
            Book Another Class
          </Link>
        </div>

        {/* Print Receipt Link */}
        <p className="text-center mt-6 text-sm text-warm-500">
          <button
            onClick={() => window.print()}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            Print Receipt
          </button>
        </p>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-warm-200 mt-12 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-warm-500 text-sm">
            Questions about your booking?{' '}
            <a href="mailto:support@stagedooraccess.com" className="text-primary-600 hover:text-primary-700">
              Contact Support
            </a>
          </p>
          <p className="text-warm-400 text-xs mt-2">
            Booked on {new Date(confirmation.bookedAt).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })}
          </p>
        </div>
      </footer>
    </div>
  );
}
